import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MetricValue } from '@hospasense/contracts';
import { fetchMetrics } from '../lib/queries-s5.js';
import {
  Badge,
  Card,
  CardBody,
  ErrorState,
  LoadingState,
  PageHeader,
} from '../components/ui/index.js';

/** Where each metric's drill-through lands: the operational list behind it. */
const DRILL_THROUGH: Record<string, { to: string; label: string }> = {
  activation_rate: { to: '/onboarding', label: 'Onboarding pipeline' },
  time_to_value_days: { to: '/onboarding', label: 'Onboarding pipeline' },
  fleet_adoption_current_release: { to: '/releases', label: 'Releases & rollout' },
  retention_rate: { to: '/organisations', label: 'Organisation registry' },
  support_burden_per_org: { to: '/support', label: 'Support queue' },
  sla_attainment: { to: '/support', label: 'Support queue' },
  expansion_signals: { to: '/entitlements', label: 'Entitlement drift' },
  invoice_collection_rate: { to: '/billing', label: 'B2B invoicing' },
};

function formatValue(m: MetricValue): string {
  if (m.value === null) return '—';
  if (m.unit === 'percent') return `${m.value}%`;
  if (m.unit === 'days') return `${m.value} days`;
  if (m.unit === 'ratio') return `${m.value}×`;
  return String(m.value);
}

export function AnalyticsPage() {
  const [metrics, setMetrics] = useState<MetricValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setMetrics(await fetchMetrics());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <PageHeader
        title="Product Analytics"
        subtitle="Privacy-preserving aggregates from Control's own operational tables. Every figure ships its numerator, denominator, timeframe and exclusions — nothing here inspects clinical content."
      />
      {error && <ErrorState description={error} onRetry={() => void load()} />}
      {loading ? (
        <LoadingState label="Computing metrics…" />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {metrics.map((m) => (
            <Card key={m.key} data-testid={`metric-${m.key}`}>
              <CardBody>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-text-primary">{m.title}</h2>
                    <p
                      className={`mt-1 text-3xl font-bold tabular-nums ${m.incomplete ? 'text-text-muted' : 'text-text-primary'}`}
                    >
                      {formatValue(m)}
                    </p>
                  </div>
                  {m.incomplete ? (
                    <Badge tone="warning">incomplete source</Badge>
                  ) : (
                    <Badge tone="success">live</Badge>
                  )}
                </div>
                {m.incomplete && m.incompleteReason && (
                  <p className="mt-1 text-xs text-warning">{m.incompleteReason}</p>
                )}
                <dl className="mt-3 space-y-1 border-t border-border pt-3 text-xs">
                  <LineageRow term="Numerator" detail={m.numerator} raw={m.numeratorValue} />
                  <LineageRow term="Denominator" detail={m.denominator} raw={m.denominatorValue} />
                  <LineageRow term="Timeframe" detail={m.timeframe} />
                  <LineageRow term="Exclusions" detail={m.exclusions} />
                  <LineageRow term="Source of truth" detail={m.sourceOfTruth} mono />
                </dl>
                <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-xs">
                  <span className="text-text-muted">
                    Computed {new Date(m.computedAt).toLocaleString()}
                  </span>
                  {DRILL_THROUGH[m.key] && (
                    <Link
                      to={DRILL_THROUGH[m.key]!.to}
                      className="font-medium text-link hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    >
                      {DRILL_THROUGH[m.key]!.label} →
                    </Link>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function LineageRow({
  term,
  detail,
  raw,
  mono,
}: {
  term: string;
  detail: string;
  raw?: number | null;
  mono?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 font-semibold uppercase tracking-wide text-text-muted">
        {term}
      </dt>
      <dd className={`min-w-0 text-text-secondary ${mono ? 'font-mono' : ''}`}>
        {detail}
        {raw !== undefined && raw !== null && (
          <span className="ml-1 font-mono text-text-muted">(= {raw})</span>
        )}
      </dd>
    </div>
  );
}
